import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", message: "Confirming your payment…" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState({ status: "error", message: "Missing session ID." });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 8) {
        setState({ status: "error", message: "Payment is taking longer than expected. Please refresh shortly." });
        return;
      }
      attemptsRef.current++;
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "success", message: "Payment successful! Welcome to Pro." });
          setTimeout(() => nav("/app/billing"), 1800);
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", message: "This checkout session expired." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [nav, refresh]);

  return (
    <div className="min-h-screen bg-background grid-noise flex items-center justify-center p-4">
      <div className="brutal bg-white p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {state.status === "checking" && <Loader2 className="w-14 h-14 mx-auto animate-spin" strokeWidth={2.5} />}
        {state.status === "success" && <div className="w-14 h-14 mx-auto bg-mint border-2 border-foreground rounded-md shadow-brutal-sm flex items-center justify-center"><CheckCircle2 className="w-8 h-8" strokeWidth={2.5} /></div>}
        {state.status === "error" && <div className="w-14 h-14 mx-auto bg-destructive text-white border-2 border-foreground rounded-md shadow-brutal-sm flex items-center justify-center"><XCircle className="w-8 h-8" strokeWidth={2.5} /></div>}
        <h1 className="font-heading font-extrabold text-2xl mt-4">
          {state.status === "checking" && "One sec…"}
          {state.status === "success" && "You're Pro! 🎉"}
          {state.status === "error" && "Something went wrong"}
        </h1>
        <p className="text-sm font-medium text-muted-foreground mt-2">{state.message}</p>
        <button onClick={() => nav("/app/billing")} className="mt-6 px-4 py-2.5 border-2 border-foreground rounded-md font-bold shadow-brutal-sm">
          Back to billing
        </button>
      </div>
    </div>
  );
}
